/**
 * App entry — one of the few upstream files this overlay replaces.
 *
 * `CRM` takes dataProvider/authProvider as props and only falls back to the
 * Supabase ones as defaults, so swapping the backend needs no change inside
 * atomic-crm at all. That is why the overlay is a handful of files and not a
 * fork of the component tree.
 *
 * THE THEME IMPORT BELONGS HERE, NOT IN index.css. main.tsx does
 * `import "./index.css"` and then `import App`, so anything App pulls in lands
 * LATER in the bundle and wins on equal specificity. Put it in index.css
 * instead and upstream's `:root` is declared afterwards — the palette ships,
 * changes nothing, and looks correct in the source. test-crm-serving asserts
 * the resulting byte order for exactly this reason.
 */
import { CRM } from "@/components/atomic-crm/root/CRM";
import {
  getAuthProvider,
  getDataProvider,
} from "@/components/atomic-crm/providers/privateassistant";
import { getKeycloakToken } from "@/components/atomic-crm/providers/privateassistant/authProvider";
import "./theme.css";
/**
 * The platform mark, replacing atomic-crm's own logo.
 *
 * IMPORTED, NOT A PATH. CRM.tsx is explicit about this: the logo must be an
 * imported asset, an absolute URL or a data URI — never route-relative. Vite
 * resolves an import against the JS chunk, so it survives a reload on a nested
 * route like `/contacts/1/show`; a `"./logos/x.svg"` string is resolved against
 * the current URL and 404s there. Same failure the `--base=/` build flag exists
 * for.
 *
 * ONE ASSET FOR BOTH MODES, and the mark rather than the wordmark:
 *   - the mark is indigo on white, legible on either theme, so there is no
 *     light/dark pair to keep in step;
 *   - it is square like the logo it replaces, and the header renders the logo
 *     immediately beside `title` — a wordmark there would read
 *     "privateassistant.ai  Private Assistant CRM".
 * The wordmarks sit in ./logos/ if that trade is ever worth making; see the
 * README there, and note their upstream names are inverted.
 */
import brandMark from "./logos/privateassistant-mark.svg";

/**
 * Start in whatever mode the person chose on the rest of the platform.
 *
 * The portal writes `pa-theme` on `.privateassistant.ai`, so this subdomain can
 * read it. Arriving in light mode having just set dark two tabs over is the
 * kind of small wrongness that makes a subdomain feel like a different product.
 *
 * SEEDED ONCE, AND ONLY WHEN UNSET. The CRM has its own theme toggle whose
 * choice lives in the same key; overwriting it on every load would make that
 * toggle revert on refresh, which reads as a bug rather than as a policy. The
 * cookie is the initial default, not a continuing authority.
 *
 * Key format verified against ra-core's localStorageStore (`RaStore` + appKey
 * + "." + key, JSON-encoded) with appKey "CRM" from CRM.tsx — not guessed. A
 * near-miss writes a key nothing reads and fails silently.
 */
const seedThemeFromPortal = () => {
  const KEY = "RaStoreCRM.theme";
  try {
    if (localStorage.getItem(KEY) !== null) return; // the user has chosen
    const m = document.cookie.match(/(?:^|;\s*)pa-theme=(dark|light)\b/);
    if (m) localStorage.setItem(KEY, JSON.stringify(m[1]));
  } catch {
    // Private mode, blocked storage: the app's own default ("system") applies.
  }
};

seedThemeFromPortal();

/**
 * Route an OIDC return into react-admin's callback route.
 *
 * THERE IS DELIBERATELY NO SEPARATE CALLBACK PAGE. There used to be one --
 * `public/auth-callback.html`, Keycloak's redirect_uri -- and it never ran
 * once. The service worker this app ships registers
 *
 *     registerRoute(new NavigationRoute(createHandlerBoundToURL("index.html")))
 *
 * with no denylist, so EVERY navigation on the origin is answered from cache
 * with index.html. A second HTML document on a PWA origin is unreachable by
 * construction: the app loaded at the callback URL, found no hash, failed
 * checkAuth and rewrote the location to #/login -- so a completed sign-in
 * looked exactly like a rejected one, and nothing was logged anywhere.
 *
 * curl fetched that page correctly the whole time, which is why every
 * server-side test passed. Only a browser can see a service worker.
 *
 * So Keycloak now returns to the app root and the code is handled here.
 * One entry document, nothing for the worker to swallow.
 *
 * MODULE SCOPE, NOT AN EFFECT. main.tsx imports this module before
 * createRoot().render(), and an ES module body is evaluated before the
 * importing module continues -- so this runs before react-admin's HashRouter
 * reads the hash. Inside a component it would be too late: the router would
 * already have matched `/` and redirected to login.
 *
 * `replaceState` rather than assigning location.hash, which would push a
 * history entry and make Back return to a spent authorization code.
 */
const routeOidcReturn = () => {
  try {
    const q = new URL(window.location.href).searchParams;
    // `error` matters as much as `code`: a denied consent must reach
    // handleCallback so the reason can be shown, instead of silently
    // presenting the login screen again.
    if (!q.get("code") && !q.get("error")) return;
    if (window.location.hash.startsWith("#/auth-callback")) return;
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}${window.location.search}#/auth-callback`,
    );
  } catch {
    // A malformed URL is not worth failing the whole app over; the user lands
    // on the login screen, which is where they would have gone anyway.
  }
};

routeOidcReturn();

/**
 * Take a new deploy immediately, instead of one page load later.
 *
 * A service worker updates in the BACKGROUND: the browser fetches sw.js, sees
 * new bytes, installs, and (this worker calls skipWaiting + clientsClaim) takes
 * control -- but the page that triggered all that is still running the OLD
 * bundle it was served from cache. Only the NEXT load gets the new code.
 *
 * That one-load window is not theoretical. After the login fix shipped, a
 * returning browser loaded the stale bundle, sent the old
 * `redirect_uri=/auth-callback.html`, and Keycloak answered "Invalid parameter:
 * redirect_uri" -- a hard error page, for a deployment that was entirely
 * correct. Every future change to the auth flow, the API shape or config.json
 * has the same window.
 *
 * So when the new worker takes over, reload and pick it up.
 *
 * TWO GUARDS, BOTH LOAD-BEARING:
 *
 *   * `controller` must already exist. On a first-ever visit the worker takes
 *     control for the first time and fires this same event; reloading there is
 *     a pointless flash, and if the condition were ever wrong it would be an
 *     infinite loop on the one page a user cannot escape.
 *   * Never mid-sign-in. A reload while `?code=` is in the URL would re-enter
 *     handleCallback with an authorization code Keycloak has already spent, and
 *     the user would be told their login failed when it had not.
 */
const reloadWhenNewWorkerTakesOver = () => {
  try {
    if (!("serviceWorker" in navigator)) return;
    if (!navigator.serviceWorker.controller) return; // first-ever visit
    const q = new URL(window.location.href).searchParams;
    if (q.get("code") || q.get("error")) return;     // mid-callback
    let done = false;
    // A create or edit form holds unsaved typing. Reloading it to pick up a
    // new build would throw away someone's work to fix a bug they had not
    // noticed — so wait until they leave that route or switch tabs, and take
    // the update then.
    const onAForm = () => /#\/[^?]*\/(create|edit)\b/.test(window.location.hash);
    const reload = () => {
      if (done) return;
      if (onAForm() && document.visibilityState === "visible") return;
      done = true;
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      reload();
      window.addEventListener("hashchange", reload);
      document.addEventListener("visibilitychange", reload);
    });
  } catch {
    // Storage or SW access can throw in private mode; the old behaviour (one
    // extra load) is the fallback, which is what happened before this existed.
  }
};

/**
 * Ask for a new worker on a schedule, because a CRM tab is never reloaded.
 *
 * #401 made the app reload when a NEW service worker takes control. That only
 * helps if a new worker is ever discovered — and the browser only checks on a
 * page load. This app routes on the hash, so clicking around it for a whole
 * day fetches API responses and NEVER re-fetches the document.
 *
 * Measured, not assumed: a session ran 90 minutes across four deploys with
 * zero requests for `/`, `index.html` or `sw.js`, still executing the bundle
 * it started with, while three fixes for what it was doing sat on the server.
 * "Reload the page" is not a fix when nothing tells anyone to.
 *
 * So: check on a timer, and whenever the tab is brought back to the front —
 * the moment someone returns to it is exactly when a swap costs least.
 */
const CHECK_FOR_UPDATE_MS = 15 * 60 * 1000;

const pollForNewWorker = () => {
  try {
    if (!("serviceWorker" in navigator)) return;
    const check = () => {
      navigator.serviceWorker.getRegistration().then((r) => r?.update()).catch(() => {});
    };
    setInterval(check, CHECK_FOR_UPDATE_MS);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") check();
    });
  } catch {
    // Nothing to do: the app keeps working, it just updates on the next load.
  }
};

reloadWhenNewWorkerTakesOver();
pollForNewWorker();

const authProvider = getAuthProvider();
const dataProvider = getDataProvider(getKeycloakToken);

const App = () => (
  <CRM
    dataProvider={dataProvider}
    authProvider={authProvider}
    title="Private Assistant CRM"
    // Defaults only. A tenant that sets its own logo in Settings overrides
    // both, because ConfigurationContext prefers the stored config.
    lightModeLogo={brandMark}
    darkModeLogo={brandMark}
    // Upstream pings marmelab's telemetry endpoint from every load, sending
    // `?domain=<hostname>`. Harmless for a demo, not for tenants: their
    // browsers would announce that this company runs a CRM here, to a third
    // party none of them chose, on a page they reached to read customer data.
    // Found in a browser trace while chasing the login bug, not in a review.
    disableTelemetry
  />
);

export default App;
